/**
 * QuotaBanner (iter51) — Banner persistente de uso del plan.
 *
 * Aparece SOLO cuando:
 *   - El plan tiene `dte_limit` finito (normalmente Gratis).
 *   - El uso del mes supera el 80% del límite.
 *
 * Estados:
 *   - 80–99%: amarillo "Te quedan N DTEs este mes."
 *   - 100%+:  rojo    "Has alcanzado el límite — actualiza ahora."
 *
 * Se puede cerrar por sesión (sessionStorage) para no molestar.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { dteApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, X, ArrowUpRight, Sparkles } from "lucide-react";
import { IS_LOCAL_EDITION } from "../lib/edition";

const DISMISS_KEY = "dte_quota_banner_dismissed_at";
const DISMISS_HOURS = 6; // se vuelve a mostrar tras 6h

export default function QuotaBanner() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (IS_LOCAL_EDITION) return;
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const s = await dteApi.subscription();
        if (alive) setSub(s);
      } catch {
        /* ignorar — banner solo si hay suscripción */
      }
    })();
    return () => { alive = false; };
  }, [user]);

  useEffect(() => {
    if (IS_LOCAL_EDITION) return;
    const at = sessionStorage.getItem(DISMISS_KEY);
    if (!at) return;
    const ms = Date.now() - Number(at);
    if (ms < DISMISS_HOURS * 60 * 60 * 1000) setDismissed(true);
  }, []);

  if (IS_LOCAL_EDITION) return null;
  if (!user || !sub) return null;
  if (dismissed) return null;
  if (sub.dte_limit == null) return null; // ilimitado
  const used = Number(sub.dte_used_this_month || 0);
  const limit = Number(sub.dte_limit);
  const pct = limit ? (used / limit) * 100 : 0;
  if (pct < 80) return null;

  const isOverLimit = used >= limit;
  const isCritical = !isOverLimit && pct >= 95;
  const isUpsell = !isOverLimit && pct < 95;
  const remaining = Math.max(0, limit - used);

  const catalog = sub.planCatalog || {};
  const upgradeKey = sub.plan === "gratis" ? "pro" : "empresarial";
  const upgrade = catalog[upgradeKey];
  const upgradeLabel = upgrade?.label || (upgradeKey === "pro" ? "Pro" : "Empresarial");
  const upgradeLimit =
    upgrade?.dte_limit == null ? "ilimitados" : `${upgrade.dte_limit}/mes`;
  const upgradeDelta =
    upgrade && upgrade.price_usd != null
      ? Math.max(0, upgrade.price_usd - (catalog[sub.plan]?.price_usd || 0)).toFixed(2)
      : null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  // Paleta por estado: upsell (verde-suave) → critical (amber) → over (rojo)
  const tone = isOverLimit
    ? "bg-rose-600 text-white border-rose-700"
    : isCritical
    ? "bg-amber-50 text-amber-900 border-amber-200"
    : "bg-emerald-50 text-emerald-900 border-emerald-200";
  const ctaTone = isOverLimit
    ? "bg-white text-rose-700 hover:bg-rose-50"
    : isCritical
    ? "bg-amber-900 text-amber-50 hover:bg-amber-800"
    : "bg-emerald-700 text-white hover:bg-emerald-800";
  const xTone = isOverLimit
    ? "hover:bg-rose-700"
    : isCritical
    ? "hover:bg-amber-100"
    : "hover:bg-emerald-100";

  return (
    <div
      data-testid="quota-banner"
      data-quota-state={isOverLimit ? "over" : isCritical ? "critical" : "upsell"}
      className={`w-full text-[13px] border-b ${tone}`}
    >
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-2 flex items-center gap-3">
        {isUpsell ? (
          <Sparkles className="w-4 h-4 shrink-0" strokeWidth={2} />
        ) : (
          <AlertTriangle className="w-4 h-4 shrink-0" strokeWidth={2} />
        )}
        <div className="flex-1 min-w-0 truncate">
          {isOverLimit ? (
            <span data-testid="quota-banner-text-over">
              <strong>Has alcanzado los {used}/{limit} DTE</strong> de tu plan{" "}
              <strong>{sub.planLabel}</strong>. No podrás emitir más este mes
              hasta que actualices tu plan.
            </span>
          ) : isCritical ? (
            <span data-testid="quota-banner-text-warn">
              Has emitido <strong>{used}/{limit} DTE</strong> este mes en tu
              plan <strong>{sub.planLabel}</strong> · te quedan{" "}
              <strong>{remaining}</strong> antes del bloqueo.
            </span>
          ) : (
            <span data-testid="quota-banner-text-upsell">
              Vas excelente: <strong>{used}/{limit} DTE</strong> este mes en tu
              plan <strong>{sub.planLabel}</strong>. Si necesitas espacio para
              crecer, da el salto a <strong>{upgradeLabel}</strong> ({upgradeLimit}
              {upgradeDelta ? ` · solo $${upgradeDelta} más` : ""}).
            </span>
          )}
        </div>
        <Link
          to="/suscripcion"
          data-testid="quota-banner-cta"
          className={`inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded-md transition-colors shrink-0 ${ctaTone}`}
        >
          {isOverLimit ? "Mejorar plan" : isCritical ? "Mejorar plan" : "Ver planes"}
          <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Cerrar"
          data-testid="quota-banner-dismiss"
          className={`p-1 rounded transition-colors shrink-0 ${xTone}`}
        >
          <X className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
